import { useMemo, useState } from 'react';

import { OrganizationSsoMethod } from '../../../../../typings/enums';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { window } from '../../../../globals';
import AccessGate from '../../../generic/Gates/AccessGate';
import Text from '../../../generic/Text';
import Header from '../../../shared/settings/SettingsPageHeader';
import { ssoFeature } from '../../billing/features/consts';

import AuthenticationModePicker from './AuthenticationModePicker';
import EmailAddressConfiguration from './EmailConfiguration';
import useAuthenticationSettingsContext from './hooks/useAuthenticationSettingsContext';
import LoginSubdomain from './LoginSubdomain';
import ModalPromptClaimDomain from './ModalPromptClaimDomain';
import SSOConfiguration from './SSOConfiguration';
import { defaultLDAPConfig } from './SSOConfiguration/LDAP/ldapConfigHelpers';
import { AuthenticationMode } from './types';
import { getAuthenticationMethod, getAuthenticationModeFromSettings, switchSSO } from './utils';

const useAccessLevelRequired = (mode: AuthenticationMode) =>
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Disabled for now but we should check if this is necessary.
  useMemo(() => (mode !== AuthenticationMode.Email ? 'premium' : 'enterprise'), []);

const showLoginSubdomain = () => window.customerDomain && window.customerDomain !== 'null';

function requireEmailConfiguration(mode: AuthenticationMode) {
  return [AuthenticationMode.Email, AuthenticationMode.SsoEmail].includes(mode);
}

function requireSSOConfiguration(mode: AuthenticationMode) {
  return [AuthenticationMode.Sso, AuthenticationMode.SsoEmail].includes(mode);
}

const AuthenticationSettingsPage = () => {
  const {
    settings,
    domains,
    onUpdateSettings,
    onDeleteDomain,
    onDeleteSAML,
    onSaveLDAP,
    onUpdateLDAP,
    onUpdateSAML,
    isSaveButtonDisabled,
    onSaveSettings,
    isSaving,
    hasSamlDomain,
    hasNewConfig,
    router,
  } = useAuthenticationSettingsContext();
  const [mode, setMode] = useState(getAuthenticationModeFromSettings(settings, domains));
  const [method, setMethod] = useState(getAuthenticationMethod(settings));
  const [shouldShowModalPromptClaimDomain, setShouldShowModalPromptClaimDomain] = useState(false);
  const { onConfirm } = useDialogContext();
  const accessLevelRequired = useAccessLevelRequired(mode);
  const onSelectMode = async (newMode: AuthenticationMode) => {
    await switchSSO({
      settings,
      currentMode: mode,
      newMode,
      currentMethod: method,
      newMethod: method,
      onConfirm,
      setMode,
      setMethod,
      claimedDomains: domains,
      deleteDomain: onDeleteDomain,
      deleteSaml: onDeleteSAML,
      saveLDAP: onSaveLDAP,
      //  These two only update local state
      onUpdateLDAP,
      onUpdateSAML,
    });

    const disableInviteModes = new Set([AuthenticationMode.Sso, AuthenticationMode.OAuth]);
    const isCurrentModeDisableInvite = disableInviteModes.has(mode);
    const isNewModeDisableInvite = disableInviteModes.has(newMode);

    if (isCurrentModeDisableInvite !== isNewModeDisableInvite) {
      onUpdateSettings({
        disableInvite: isNewModeDisableInvite,
      });
    }
  };

  const onSelectMethod = async (newMethod: OrganizationSsoMethod) => {
    await switchSSO({
      settings,
      currentMode: mode,
      newMode: mode,
      currentMethod: method,
      newMethod,
      onConfirm,
      setMode,
      setMethod,
      claimedDomains: domains,
      deleteDomain: onDeleteDomain,
      deleteSaml: onDeleteSAML,
      saveLDAP: onSaveLDAP,
      //  These two only update local state
      onUpdateLDAP,
      onUpdateSAML,
    });

    // If the user switches to the LDAP auth method but LDAP isn't enabled, enable it.
    if (newMethod === OrganizationSsoMethod.LDAP && !settings.ldap?.enabled) {
      onUpdateLDAP({
        ...defaultLDAPConfig,
        enabled: true,
      });
    }
  };

  const onSave = () => {
    if (!hasSamlDomain && hasNewConfig) {
      setShouldShowModalPromptClaimDomain(true);
      return null;
    }
    return onSaveSettings();
  };

  return (
    <AccessGate level={accessLevelRequired} behavior="showPage" focusFeature={ssoFeature}>
      <Header heading="Authentication Settings">
        <Header.SaveButton isDisabled={isSaveButtonDisabled} onClick={onSave} isLoading={isSaving} />
      </Header>
      <Text.H3>Authentication Method</Text.H3>
      <AuthenticationModePicker mode={mode} onSelectMode={onSelectMode} />
      {showLoginSubdomain() && <LoginSubdomain />}
      {requireEmailConfiguration(mode) && <EmailAddressConfiguration />}
      {requireSSOConfiguration(mode) && <SSOConfiguration onSelectMethod={onSelectMethod} method={method} />}
      <ModalPromptClaimDomain
        isOpen={shouldShowModalPromptClaimDomain}
        loginSubdomain={settings.loginSubdomain}
        onSaveChanges={async () => {
          await onSaveSettings();
          setShouldShowModalPromptClaimDomain(false);
        }}
        onConfigureDomain={() => {
          router.push('/account/settings/organization/authentication?claimDomain=true');
          setShouldShowModalPromptClaimDomain(false);
        }}
      />
    </AccessGate>
  );
};

export default AuthenticationSettingsPage;
